import { t } from './i18n.js';
/**
 * Killfeed — top right, newest at the bottom, five visible at most.
 *
 * Rows are plain DOM appended on an event and removed on a timer; there is no
 * per-frame DOM work at all. The fade in/out rides CSS transitions, and the
 * enter transition is armed with `requestAnimationFrame` rather than a forced
 * reflow so nothing here ever reads layout.
 */

export const TUNING = {
  max: 5,
  life: 5.0, // s before a row starts fading
  fade: 0.32, // s of fade-out (must match .kf-row.is-out in hud.css)
};

const SKULL_PATH =
  'M8 0C3.6 0 0 3.4 0 7.6c0 2.5 1.3 4.7 3.3 6v1.6c0 .4.4.8.9.8h1.5v-1.7c0-.3.2-.5.5-.5s.5.2.5.5V16h2.6v-1.7c0-.3.2-.5.5-.5s.5.2.5.5V16h1.5c.5 0 .9-.4.9-.8v-1.6c2-1.3 3.3-3.5 3.3-6C16 3.4 12.4 0 8 0zM5 9.4c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3 1.9L6.9 9h2.2L8 11.3zm3 -1.9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z';

function skullIcon() {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('class', 'kf-skull');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', SKULL_PATH);
  svg.appendChild(path);
  return svg;
}

export class Killfeed {
  constructor(ctx, root) {
    this.ctx = ctx;
    this.el = document.createElement('div');
    this.el.className = 'hud-killfeed hud-fade';
    root.appendChild(this.el);

    this.rows = []; // { el, t }
    this._sawFeedEvent = false;

    ctx.events.on('ui:killfeed', (p) => {
      this._sawFeedEvent = true;
      // A matching `actor:death` may have already queued a fallback row this
      // frame; the authored line always wins.
      clearTimeout(this._pendingFallback);
      this._pendingFallback = 0;
      // A key when the emitter has one, a finished string otherwise. The former
      // is the only form that can be read in both languages.
      const line = p?.key ? t(p.key, p.params ?? null) : p?.text ?? t('feed.eliminated');
      this.push(line, p?.headshot, p?.weapon, p?.notice);
    });

    // Until the AI director publishes proper killfeed lines, synthesise them.
    // Deferred by a tick so an authored line emitted in the same frame — in
    // either order — cancels it instead of producing a duplicate row.
    ctx.events.on('actor:death', (p) => {
      if (this._sawFeedEvent) return;
      // Your own death is announced by the death screen, not by a feed row telling
      // you that you have been eliminated. `actor:death` covers everyone now, so
      // this case has to be excluded here rather than by never being emitted.
      if (p?.actor === ctx.player) return;
      const name = p?.actor?.name ?? p?.actor?.id ?? t('feed.enemyKilled');
      const hs = p?.headshot;
      clearTimeout(this._pendingFallback);
      this._pendingFallback = setTimeout(() => {
        this._pendingFallback = 0;
        if (this._sawFeedEvent) return;
        this.push(String(name).toUpperCase(), hs);
      }, 0);
    });
  }

  /**
   * `text` is the victim; the killer is always the player for now.
   *
   * `notice` rows carry no killer and no weapon. The feed's row is a sentence —
   * "YOU · weapon · victim" — and objective news is not that sentence: routing
   * "C NEUTRALISED" through it produced the line `YOU AK-27 C NEUTRALISED`, which
   * claims the player neutralised a flag with a rifle. Anything that is not a kill
   * gets the plain row.
   */
  push(text, headshot, weapon, notice = false) {
    const row = document.createElement('div');
    row.className = notice ? 'kf-row is-notice' : headshot ? 'kf-row is-hs' : 'kf-row';

    if (!notice) {
      const you = document.createElement('b');
      you.className = 'kf-you';
      you.textContent = t('feed.you');
      row.appendChild(you);

      const wep = document.createElement('span');
      wep.className = 'kf-wep';
      wep.textContent = this._weaponName(weapon);
      row.appendChild(wep);
    }

    // Inline SVG rather than the ☠ codepoint: no font on the stack is
    // guaranteed to have it, and a tofu box in the killfeed is unforgivable.
    if (headshot && !notice) row.appendChild(skullIcon());

    const victim = document.createElement('span');
    victim.textContent = text;
    row.appendChild(victim);

    this.el.appendChild(row);
    // Arm the enter transition on the next frame — no layout read required.
    requestAnimationFrame(() => row.classList.add('is-in'));

    const entry = { el: row, t: 0 };
    this.rows.push(entry);

    while (this.rows.length > TUNING.max) {
      const old = this.rows.shift();
      this._retire(old, true);
    }
  }

  _weaponName(explicit) {
    if (explicit) return String(explicit).toUpperCase();
    const w = this.ctx.weapons;
    const n =
      w?.current?.def?.name ??
      w?.current?.displayName ??
      w?.current?.name ??
      w?.currentName ??
      w?.weaponName;
    return n ? String(n).toUpperCase() : '▸';
  }

  _retire(entry, immediate) {
    if (!entry || entry.dying) return;
    entry.dying = true;
    entry.el.classList.add('is-out');
    setTimeout(() => entry.el.remove(), TUNING.fade * 1000);
  }

  update(dt) {
    if (!this.rows.length) return;
    for (let i = this.rows.length - 1; i >= 0; i--) {
      const r = this.rows[i];
      r.t += dt;
      if (r.t >= TUNING.life) {
        this._retire(r, false);
        this.rows.splice(i, 1);
      }
    }
  }
}
